import React, { useEffect, useState } from 'react';
import { Download } from 'lucide-react';

import { partnerAccountAPI } from '../../lib/partnerAccountApi';
import { messageErreurExport } from '../../lib/exportsApi';
import { Aide } from '../ui/aide';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from '../ui/sonner';

const aujourdhui = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * « Relevé de compte »: le PDF à faire signer au partenaire. Le début est
 * proposé à la première opération du compte, la fin à aujourd'hui; la fin ne
 * peut pas précéder le début.
 */
export default function ReleveCompteDialog({ ouvert, onOpenChange, societeId, premiereOperation }) {
  const [du, setDu] = useState('');
  const [au, setAu] = useState('');
  const [envoi, setEnvoi] = useState(false);

  useEffect(() => {
    if (!ouvert) return;
    const fin = aujourdhui();
    const debut = premiereOperation ? String(premiereOperation).slice(0, 10) : fin;
    setDu(debut);
    setAu(fin);
  }, [ouvert, premiereOperation]);

  const finAvantDebut = Boolean(du && au && au < du);
  const incomplet = !du || !au;

  const telecharger = async () => {
    if (incomplet || finAvantDebut) return;
    setEnvoi(true);
    try {
      const nom = await partnerAccountAPI.telechargerReleve(societeId, { du, au });
      toast.success(`Relevé téléchargé : ${nom}`);
      onOpenChange(false);
    } catch (erreur) {
      toast.error(await messageErreurExport(erreur, 'Le relevé de compte n’a pas pu être préparé'));
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <Dialog open={ouvert} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="releve-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Relevé de compte
            <Aide titre="Relevé de compte">
              Le relevé reprend toutes les opérations de la période, avec le reste dû au début et à la fin, et deux zones de signature.
            </Aide>
          </DialogTitle>
          <DialogDescription className="sr-only">Choisir la période du relevé de compte</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="releve-du">Début</Label>
            <Input id="releve-du" type="date" value={du} max={au || undefined} onChange={(e) => setDu(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="releve-au">Fin</Label>
            <Input id="releve-au" type="date" value={au} min={du || undefined} onChange={(e) => setAu(e.target.value)} />
          </div>
        </div>
        {finAvantDebut ? (
          <p role="alert" className="text-sm text-destructive" data-testid="releve-erreur-dates">
            La fin doit être après le début.
          </p>
        ) : null}
        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button type="button" onClick={telecharger} disabled={envoi || incomplet || finAvantDebut} data-testid="releve-telecharger">
            <Download className="h-4 w-4" aria-hidden="true" />
            {envoi ? 'Préparation…' : 'Télécharger le PDF'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
